import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import { getAssetsAndLiabilities, createFinancialRecord, deleteFinancialRecord, updateFinancialRecord } from "../../util/APIUtils";
import AssetsLiabilitiesList from './AssetsLiabilitiesList';
import AssetsLiabilitiesForm from './AssetsLiabilitiesForm';

const AssetsLiabilities = () => {
    const [records, setRecords] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    useEffect(() => {
        fetchRecords();
    }, []);

    const fetchRecords = async () => {
        try {
            setIsLoading(true);
            const data = await getAssetsAndLiabilities();
            setRecords(data);
        } catch (error) {
            swal("Error", error.message || "Failed to fetch records", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        setShowForm(true);
    };

    const handleSubmit = async (formData) => {
        try {
            setIsSubmitting(true);
            setIsLoading(true);
            setError(null);

            const formDataToSend = new FormData();

            // Convert form data to JSON string, excluding the file
            const recordData = {
                recordType: formData.recordType,
                categoryId: Number(formData.categoryId),
                name: formData.name,
                description: formData.description,
                amount: Number(formData.amount),
                location: formData.location,
                owned: formData.owned,
                lender: formData.lender,
                dueDate: formData.dueDate,
                recordStatus: formData.recordStatus
            };

            // Add the record JSON as a string parameter
            formDataToSend.append('record', JSON.stringify(recordData));

            // Add the file if present with the correct parameter name
            if (selectedFile) {
                formDataToSend.append('file', selectedFile);
            }

            if (editingRecord) {
                await updateFinancialRecord(editingRecord.recordId, formDataToSend);
                swal("Success", "Record updated successfully", "success");
            } else {
                await createFinancialRecord(formDataToSend);
                swal("Success", "Record created successfully", "success");
            }

            setShowForm(false);
            setEditingRecord(null);
            setSelectedFile(null);
            await fetchRecords();  // Make sure we wait for the records to refresh
        } catch (error) {
            swal("Error", error.message || 'Failed to save record', "error");
            setError(error.message);
        } finally {
            setIsSubmitting(false);
            setIsLoading(false);
        }
    };

    const handleDelete = async (record) => {
        try {
            setIsLoading(true);
            const willDelete = await swal({
                title: "Are you sure?",
                text: `Do you want to delete the ${record.recordType.toLowerCase()} "${record.name}"?`,
                icon: "warning",
                buttons: true,
                dangerMode: true,
            });

            if (willDelete) {
                try {
                    const response = await deleteFinancialRecord(record.recordId);
                    // Check if response is ok, don't try to parse JSON
                    if (response.ok) {
                        swal("Success", "Record deleted successfully", "success");
                        await fetchRecords();  // Refresh the grid after successful deletion
                    } else {
                        swal("Error", "Failed to delete record", "error");
                    }
                } catch (error) {
                    swal("Error", error.message || 'Failed to delete record', "error");
                }
            }
        } catch (error) {
            swal("Error", "Failed to delete record", "error");
        } finally {
            setIsLoading(false);
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(amount);
    };

    return (
        <div className="animated fadeIn" style={{ position: 'relative' }}>
            {isLoading && (
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'rgba(255,255,255,0.7)',
                    zIndex: 1000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <span className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status" />
                </div>
            )}
            {showForm ? (
                <AssetsLiabilitiesForm
                    onSubmit={handleSubmit}
                    onCancel={() => {
                        setShowForm(false);
                        setEditingRecord(null);
                    }}
                    onFileChange={(file) => setSelectedFile(file)}
                    isSubmitting={isSubmitting || isLoading}
                    error={error}
                    initialData={editingRecord}
                />
            ) : (
                <AssetsLiabilitiesList
                    records={records}
                    onAddNew={() => {
                        setEditingRecord(null);
                        setShowForm(true);
                    }}
                    formatAmount={formatAmount}
                    onRefresh={fetchRecords}
                    isLoading={isLoading}
                    onDelete={handleDelete}
                    onEdit={handleEdit}
                />
            )}
        </div>
    );
};

export default AssetsLiabilities;



// WEBPACK FOOTER //
// ./src/components/CustomComponents/AssetsLiabilities.js